import React, { useState, useEffect, useRef } from 'react'
import Img from 'src/components/shared/Img'
import { numberFormat } from 'src/common/utils'

const useCountAnimation = (targetValue, duration = 500) => {
  const [displayValue, setDisplayValue] = useState(targetValue)
  const animationRef = useRef(null)
  const currentValueRef = useRef(targetValue)

  useEffect(() => {
    if (animationRef.current) cancelAnimationFrame(animationRef.current)

    const startValue = currentValueRef.current
    const endValue = targetValue ?? 0

    if (startValue === endValue) return

    let startTime = null

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp
      const elapsed = timestamp - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const current = startValue + (endValue - startValue) * eased

      currentValueRef.current = current
      setDisplayValue(current)

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate)
      } else {
        currentValueRef.current = endValue
        setDisplayValue(endValue)
      }
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [targetValue, duration])

  return displayValue
}

const monthElement = (
  <span className="text-center text-base font-normal leading-[140%] text-content-primary">
    /month
  </span>
)

const BarDetail = ({
  currentMinPayment,
  reliefMonthlyPayment,
  amountFormatOptions,
  heightBar = 280,
}) => {
  const animatedRelief = useCountAnimation(reliefMonthlyPayment)
  const animatedCurrent = useCountAnimation(currentMinPayment)

  const reliefBarWidth = currentMinPayment
    ? (reliefMonthlyPayment / currentMinPayment) * heightBar
    : 0

  const getAmountElement = (amount, formatOptions) => (
    <p className="text-center text-2xl font-bold leading-[130%] text-content-primary lg:text-heading-2">
      {numberFormat(amount, formatOptions)}
    </p>
  )

  return (
    <div className="size-full flex items-end gap-4 bg-white lg:gap-8">
      <div
        className="relative flex w-full flex-1 flex-col items-center justify-end gap-2 rounded-lg bg-[#EBF0FC] p-4 transition-all duration-500 ease-in-out lg:gap-[10px] lg:px-6 lg:py-8"
        style={{ height: `${reliefBarWidth}px`, maxHeight: `${heightBar}px` }}
      >
        <Img
          src="/next-assets/icons/check-mark-blue.svg"
          alt="Checkmark icon"
          width={48}
          height={48}
          layout="intrinsic"
          className="size-[40px] lg:size-12"
        />
        <div className="flex flex-col items-center">
          {getAmountElement(animatedRelief, amountFormatOptions)}
          {monthElement}
        </div>
        <div
          className="absolute flex w-full justify-center gap-2 text-center  lg:gap-2"
          style={{ bottom: 'calc(100% + 16px)' }}
        >
          <span className="inline-block h-[23px] translate-y-[50%] text-base font-normal not-italic leading-[140%] text-content-primary lg:translate-y-[calc(50%--9px)]">
            With
          </span>
          <Img
            src="/next-assets/icons/fdr-logo-icon.svg"
            alt="FDR logo icon"
            width={80}
            height={46}
            layout="intrinsic"
            className="h-[35px] min-h-[35px] w-15 min-w-15 lg:h-[46px] lg:w-20"
          />
        </div>
      </div>
      <div
        className="size-full relative flex flex-1 flex-col items-center justify-end rounded-lg bg-[#FAE8EB] p-4 lg:px-6 lg:py-8"
        style={{ maxHeight: `${heightBar}px` }}
      >
        {getAmountElement(animatedCurrent, amountFormatOptions)}
        {monthElement}
        <p
          className="absolute text-center text-base font-normal not-italic leading-[140%]"
          style={{ bottom: 'calc(100% + 16px)' }}
        >
          Current min. payments to creditors
        </p>
      </div>
    </div>
  )
}

export default BarDetail
